import React, { useEffect, useState } from 'react';
import Lottie from 'react-lottie';
import Text from '@common/text';
import Button from '@common/button';
import Icon from '@common/assets';
import Image from '@common/image';
import { useUserInfo } from '@repository/auth/useUserInfo';
import NHISTermList from '@components/profile/modals/terms/nhisTermList';
import barChart from '@graphic/barChart.json';
import { useTerms } from '@repository/terms/useTerms';
import Colors from '@domain/constant/colors';
import { FontStyle } from '@domain/constant/fontStyle';
import { TERMS } from '@domain/constant/termsType';
import classnames from 'classnames/bind';
import styles from './index.module.scss';

const cx = classnames.bind(styles);

const options = {
  loop: true,
  autoplay: true,
  animationData: barChart,
};

const getInitialState = (status = false, length = 3) => {
  return Array.from({ length }, () => status);
};

interface Step1Props {
  nextStep: () => void;
}

const Step1 = ({ nextStep }: Step1Props) => {
  const [checkStatus, setCheckStatus] = useState(getInitialState());
  const { data: userData } = useUserInfo();
  const isLogin = !!userData?.id;
  const { data: termsData } = useTerms({
    termType: TERMS.NHIS_PRIVACY_PROVISION,
    termRequired: true,
    options: { enabled: isLogin },
  });

  useEffect(() => {
    if (!termsData) return;
    setCheckStatus(getInitialState(false, termsData.length));
  }, [termsData]);

  const onCheck = ({ idx }: { idx: number }) => {
    setCheckStatus((prevCheckStatus) => {
      const postCheckStatus = prevCheckStatus;
      postCheckStatus[idx] = !prevCheckStatus[idx];
      return [...postCheckStatus];
    });
  };

  const isNotAgreeRequiredTerms = termsData?.some((item, idx) => {
    if (item.isRequired) return !checkStatus[idx];
    return false;
  });

  return (
    <div className={cx('area')}>
      <div className={cx('wrap')}>
        <div className={cx('titleArea')}>
          <Text fontStyle={FontStyle.B2_M} color={Colors.C_GREEN_200}>
            내가 언제 입사했더라? 🤔
          </Text>
          <Text fontStyle={FontStyle.H5_B} color={Colors.C_COOL_GRAY_90}>
            잊어버린 경력도 1분 만에 불러올 수 있어요.
          </Text>
        </div>

        <div className={cx('contentsArea')}>
          <Lottie options={options} isClickToPauseDisabled />
        </div>

        <ul className={cx('listArea')}>
          <li className={cx('itemArea')}>
            <Image
              src={'/img/v2/components/profile/nhisCareerModal/iconNHIS.png'}
              alt={'nhis'}
              width={32}
              height={32}
            />
            <Text fontStyle={FontStyle.B3_M}>건강보험공단에서 경력, 소득 정보를 불러와요.</Text>
          </li>
          <li className={cx('itemArea')}>
            <Icon name={'resumeDoneGreen'} width={32} height={32} />
            <Text fontStyle={FontStyle.B3_M}>지금까지 근무했던 모든 회사의 경력을 확인할 수 있어요.</Text>
          </li>
          <li className={cx('itemArea')}>
            <Icon name={'matchingPersonGreen'} width={32} height={32} />
            <Text fontStyle={FontStyle.B3_M}>불러온 경력 중 원하는 경력만 선택해서 등록할 수 있어요.</Text>
          </li>
        </ul>

        {termsData && (
          <div className={cx('termListArea')}>
            <NHISTermList termList={termsData} checkStatus={checkStatus} onCheck={onCheck} />
          </div>
        )}
      </div>

      <div className={cx('btnArea')}>
        <Button className={cx('btn')} type={'button'} onClick={nextStep} disabled={isNotAgreeRequiredTerms}>
          계속
        </Button>
      </div>
    </div>
  );
};

export default Step1;
